import { useState, useEffect, useMemo } from "react";
import {
  ArrowLeftRight,
  CircleHelp,
  Ellipsis,
  EyeOff,
  Maximize2,
  Minimize2,
  PanelLeft,
  PanelRight,
} from "lucide-react";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import { IconButton } from "~/components/assistant/PopoverButton";
import { ZENDESK_AI_ASSISTANT_HELP_URL } from "~/components/assistant/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { DatasetAssistant } from "./DatasetAssistant";
import { DatasetAssistantV2 } from "./assistant-v2/DatasetAssistantV2";
import {
  type AssistantPosition,
  type AssistantVersion,
  loadDatasetAssistantState,
  updateDatasetAssistantState,
} from "./datasetAssistantState";

const MIN_WIDTH = 320;
const MAX_WIDTH = 1024;
const DEFAULT_WIDTH_RATIO = 0.4;

interface DatasetAssistantPanelProps {
  datasetId: string;
  isCollapsed: boolean;
  onCollapse: () => void;
  showV1: boolean;
  showV2: boolean;
}

function resolveInitialVersion(
  storedVersion: AssistantVersion,
  showV1: boolean,
  showV2: boolean
): AssistantVersion {
  if (storedVersion === "v2" && showV2) {
    return "v2";
  }
  if (storedVersion === "v1" && showV1) {
    return "v1";
  }
  if (showV2) {
    return "v2";
  }
  return "v1";
}

function resolveDefaultWidth(): number {
  if (typeof window === "undefined") {
    return MIN_WIDTH;
  }

  const viewportBasedWidth = Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO);
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportBasedWidth));
}

function resolveWidthFromStoredRatio(storedRatio: number): number {
  if (typeof window === "undefined") {
    return MIN_WIDTH;
  }

  if (!Number.isFinite(storedRatio) || storedRatio <= 0 || storedRatio > 1) {
    return resolveDefaultWidth();
  }

  const viewportBasedWidth = Math.round(window.innerWidth * storedRatio);
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportBasedWidth));
}

export function DatasetAssistantPanel({
  datasetId,
  isCollapsed,
  onCollapse,
  showV1,
  showV2,
}: DatasetAssistantPanelProps) {
  const initialAssistantState = useMemo(() => loadDatasetAssistantState(), []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeDragCleanup, setActiveDragCleanup] = useState<(() => void) | null>(null);
  const [activeVersion, setActiveVersion] = useState<AssistantVersion>(() =>
    resolveInitialVersion(initialAssistantState.version, showV1, showV2)
  );
  const [assistantPosition, setAssistantPosition] = useState<AssistantPosition>(
    initialAssistantState.position
  );
  const [panelWidth, setPanelWidth] = useState(() => {
    return resolveWidthFromStoredRatio(initialAssistantState.widthRatio);
  });

  const canSwitch = showV1 && showV2;

  const switchVersion = () => {
    const next = activeVersion === "v1" ? "v2" : "v1";
    setActiveVersion(next);
    updateDatasetAssistantState({ version: next });
  };

  useEffect(() => {
    const storedVersion = loadDatasetAssistantState().version;
    const resolvedVersion = resolveInitialVersion(storedVersion, showV1, showV2);
    setActiveVersion((currentVersion) =>
      currentVersion === resolvedVersion ? currentVersion : resolvedVersion
    );
  }, [showV1, showV2]);

  const togglePosition = () => {
    const next = assistantPosition === "right" ? "left" : "right";
    setAssistantPosition(next);
    updateDatasetAssistantState({ position: next });
  };

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  useEffect(() => {
    return () => {
      activeDragCleanup?.();
    };
  }, [activeDragCleanup]);

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    activeDragCleanup?.();

    const startX = e.clientX;
    const startWidth = panelWidth;
    const panelEl = e.currentTarget.parentElement as HTMLDivElement | null;
    let latestWidth = startWidth;
    let pendingWidth: number | null = null;
    let rafId: number | null = null;

    const onMove = (ev: MouseEvent) => {
      const delta = assistantPosition === "right" ? startX - ev.clientX : ev.clientX - startX;
      latestWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      pendingWidth = latestWidth;

      if (rafId == null) {
        rafId = window.requestAnimationFrame(() => {
          if (panelEl && pendingWidth != null) {
            panelEl.style.width = `${pendingWidth}px`;
          }
          rafId = null;
        });
      }
    };

    const cleanup = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const onUp = () => {
      cleanup();
      const ratio = latestWidth / window.innerWidth;
      updateDatasetAssistantState({ widthRatio: ratio });
      setPanelWidth(latestWidth);
      setActiveDragCleanup(null);
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    setActiveDragCleanup(() => cleanup);
  };

  const headerActions = isFullscreen ? (
    <>
      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <IconButton
            onClick={() => setIsFullscreen(false)}
            variant="ghost"
            aria-label="Exit fullscreen"
          >
            <Minimize2 className="size-4.5 text-gray-600" />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>
          <p>Exit Fullscreen</p>
        </TooltipContent>
      </Tooltip>
    </>
  ) : (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" aria-label="More options">
            <Ellipsis className="size-4.5 text-gray-600" />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          <DropdownMenuItem
            onClick={() =>
              window.open(ZENDESK_AI_ASSISTANT_HELP_URL, "_blank", "noopener,noreferrer")
            }
          >
            <CircleHelp className="size-4 mr-2" />
            View Usage Guide
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {canSwitch && (
            <DropdownMenuItem onClick={switchVersion}>
              <ArrowLeftRight className="size-4 mr-2" />
              {activeVersion === "v1" ? "Switch to V2" : "Switch to V1"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={togglePosition}>
            {assistantPosition === "right" ? (
              <PanelLeft className="size-4 mr-2" />
            ) : (
              <PanelRight className="size-4 mr-2" />
            )}
            {assistantPosition === "right" ? "Move to Left" : "Move to Right"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCollapse}>
            <EyeOff className="size-4 mr-2" />
            Hide Analysis Agent
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <IconButton
            onClick={() => setIsFullscreen(true)}
            variant="ghost"
            aria-label="Enter fullscreen"
          >
            <Maximize2 className="size-4.5 text-gray-600" />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>
          <p>Fullscreen</p>
        </TooltipContent>
      </Tooltip>
    </>
  );

  return (
    <>
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-in fade-in duration-200"
          onClick={() => setIsFullscreen(false)}
        />
      )}

      <div
        role="region"
        aria-label="Analysis Agent"
        className={cn(
          "relative flex shrink-0 overflow-hidden",
          isCollapsed && "hidden",
          isFullscreen
            ? "fixed inset-3 z-50 rounded-2xl shadow-2xl border border-gray-200"
            : "sticky top-0"
        )}
        style={
          isFullscreen
            ? undefined
            : {
                width: panelWidth,
                height: `calc(100vh - ${HEADER_HEIGHT})`,
                order: assistantPosition === "left" ? -1 : 0,
              }
        }
      >
        {activeVersion === "v2" && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(-35deg, rgba(248, 242, 212, 0.24) 0px, rgba(248, 242, 212, 0.24) 24px, rgba(255, 255, 255, 0.92) 24px, rgba(255, 255, 255, 0.92) 52px)",
            }}
          />
        )}

        {assistantPosition === "right" && !isFullscreen && (
          <div
            onMouseDown={handleDragStart}
            className="group absolute left-0 inset-y-0 z-20 w-1.5 cursor-col-resize"
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gray-300 transition-all group-hover:w-0.5 group-hover:bg-primary group-active:w-0.5 group-active:bg-primary" />
          </div>
        )}

        {/* Assistant fills the remaining space */}
        <div className="relative z-10 w-full h-full bg-white/95">
          {activeVersion === "v1" ? (
            <DatasetAssistant
              datasetId={datasetId}
              mode="embedded"
              showSidebar
              compactMode={!isFullscreen}
              headerActions={headerActions}
            />
          ) : (
            <DatasetAssistantV2
              datasetId={datasetId}
              compactMode={!isFullscreen}
              headerActions={headerActions}
            />
          )}
        </div>

        {assistantPosition === "left" && !isFullscreen && (
          <div
            onMouseDown={handleDragStart}
            className="group absolute right-0 inset-y-0 z-20 w-1.5 cursor-col-resize"
          >
            <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gray-300 transition-all group-hover:w-0.5 group-hover:bg-primary group-active:w-0.5 group-active:bg-primary" />
          </div>
        )}
      </div>
    </>
  );
}
